import { useParams, Link } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { useBookMetadata } from '../hooks/useBooks';
import UnifiedPdfRenderer from '../components/reader/UnifiedPdfRenderer';
import UnifiedEpubRenderer from '../components/reader/UnifiedEpubRenderer';
import UnifiedCustomHtmlRenderer from '../components/reader/UnifiedCustomHtmlRenderer';
import LoadingSpinner from '../components/LoadingSpinner';
import ErrorMessage from '../components/ErrorMessage';
import { BookFormat, EpubRendererType } from '../types/book';
import type { BookMetadataDto } from '../types/book';
import { offlineContentService } from '../services/offline/OfflineContentService';
import { useAuth } from '../hooks/useAuth';
import { ExpiredLicenseScreen } from '../components/reader/ExpiredLicenseScreen';
import type { LicenseValidationResult } from '../types/offline';

export default function BookReaderPage() {
  const { bookId } = useParams<{ bookId: string }>();
  const { user } = useAuth();

  const {
    data: metadata,
    isLoading,
    error,
  } = useBookMetadata(bookId);

  // Offline metadata fallback
  const [offlineMetadata, setOfflineMetadata] = useState<BookMetadataDto | null>(null);
  const [isLoadingOffline, setIsLoadingOffline] = useState(false);
  const [licenseResult, setLicenseResult] = useState<LicenseValidationResult | null>(null);

  // Try to load offline metadata if API fails
  useEffect(() => {
    const loadOfflineMetadata = async () => {
      if (!bookId || metadata || isLoading || !error) {
        // Skip if: no bookId, already have metadata, still loading, or no error
        return;
      }

      console.log('📖 API metadata failed, trying offline storage...');
      setIsLoadingOffline(true);

      try {
        // Get offline book info
        const offlineBook = await offlineContentService.getOfflineBook(bookId);

        if (offlineBook && offlineBook.format === 'EPUB') {
          // Get chapter list
          const toc = await offlineContentService.getEpubTOC(bookId);

          // Construct metadata from offline book
          const constructedMetadata: BookMetadataDto = {
            id: bookId,
            title: offlineBook.title,
            author: offlineBook.author || 'Unknown Author',
            format: BookFormat.Epub,
            coverImageUrl: offlineBook.coverImageUrl,
            chapterCount: offlineBook.chapterCount || toc.length,
            pageCount: 0,
            isDrmEnabled: false, // Offline books are already downloaded, DRM doesn't matter for reading
            rendererType: EpubRendererType.CustomHtml, // Assume CustomHtml for offline
            chapters: toc.map(chapter => ({
              index: chapter.index,
              title: chapter.title,
            })),
          };

          setOfflineMetadata(constructedMetadata);
          console.log('💾 Loaded metadata from offline storage');
        } else if (offlineBook && offlineBook.format === 'PDF') {
          // PDF metadata
          const constructedMetadata: BookMetadataDto = {
            id: bookId,
            title: offlineBook.title,
            author: offlineBook.author || 'Unknown Author',
            format: BookFormat.Pdf,
            coverImageUrl: offlineBook.coverImageUrl,
            chapterCount: 0,
            pageCount: offlineBook.pageCount || 0,
            isDrmEnabled: false,
            rendererType: EpubRendererType.CustomHtml, // Not used for PDF
            chapters: [],
          };

          setOfflineMetadata(constructedMetadata);
          console.log('💾 Loaded PDF metadata from offline storage');
        } else {
          console.error('❌ Book not available offline');
        }
      } catch (err) {
        console.error('Failed to load offline metadata:', err);
      } finally {
        setIsLoadingOffline(false);
      }
    };

    loadOfflineMetadata();
  }, [bookId, metadata, isLoading, error]);

  useEffect(() => {
    const validateOfflineLicense = async () => {
      if (!bookId || !user?.profile?.sub || !offlineMetadata) return;

      const result = await offlineContentService.hasValidLicense(bookId, user.profile.sub);
      setLicenseResult(result);
    };

    validateOfflineLicense();
  }, [bookId, user?.profile?.sub, offlineMetadata]);

  // Use offline metadata if available
  const effectiveMetadata = metadata || offlineMetadata;
  const effectiveIsLoading = isLoading || isLoadingOffline;

  if (!bookId) {
    return (
      <div className="min-h-screen bg-neutral-50 flex items-center justify-center p-4">
        <ErrorMessage
          title="Invalid Book"
          message="No book ID provided. Please select a book to read."
        >
          <Link to="/" className="btn btn-primary mt-4">
            Go Home
          </Link>
        </ErrorMessage>
      </div>
    );
  }

  if (effectiveIsLoading) {
    return (
      <div className="min-h-screen bg-neutral-50 flex items-center justify-center">
        <LoadingSpinner message="Loading book..." />
      </div>
    );
  }

  if (offlineMetadata && licenseResult && !licenseResult.valid) {
    return <ExpiredLicenseScreen expirationDate={licenseResult.expirationDate} />;
  }

  if (!effectiveMetadata) {
    return (
      <div className="min-h-screen bg-neutral-50 flex items-center justify-center p-4">
        <ErrorMessage
          title="Book Not Found"
          message="Unable to load this book. It may have been deleted, you may not have permission to access it, or it's not available offline."
        >
          <Link to="/" className="btn btn-primary mt-4">
            Go Home
          </Link>
        </ErrorMessage>
      </div>
    );
  }

  // Route to appropriate unified renderer based on book format.
  // All renderers are DRM-aware and handle DRM internally.
  //
  // ⚠️ Dual EPUB renderer architecture — do not collapse into one:
  //   • CustomHtml → UnifiedCustomHtmlRenderer.tsx  (simple reflowable EPUBs,
  //                  ~90-95% of the corpus — default today)
  //   • EpubJs     → UnifiedEpubRenderer.tsx        (complex EPUBs: fixed-layout,
  //                  media overlays, scripts, CFI, RTL/vertical text — ~5-10%)
  // Auto-selection is intended (see EpubComplexityAnalyzer.cs) but currently
  // stubbed, so every new book is forced to CustomHtml at upload time.
  // When adding/changing reader features, update BOTH renderers.
  if (effectiveMetadata.format === BookFormat.Pdf) {
    return <UnifiedPdfRenderer bookId={bookId} isDrmEnabled={effectiveMetadata.isDrmEnabled} />;
  } else if (effectiveMetadata.format === BookFormat.Epub) {
    if (effectiveMetadata.rendererType === EpubRendererType.CustomHtml) {
      return <UnifiedCustomHtmlRenderer bookId={bookId} isDrmEnabled={effectiveMetadata.isDrmEnabled} />;
    } else {
      return <UnifiedEpubRenderer bookId={bookId} isDrmEnabled={effectiveMetadata.isDrmEnabled} />;
    }
  } else {
    return (
      <div className="min-h-screen bg-neutral-50 flex items-center justify-center p-4">
        <ErrorMessage
          title="Unsupported Format"
          message={`Book format "${effectiveMetadata.format}" is not supported.`}
        >
          <Link to="/" className="btn btn-primary mt-4">
            Go Home
          </Link>
        </ErrorMessage>
      </div>
    );
  }
}
